import { useSelector, useDispatch } from 'react-redux'
import useApiStatusDispatch from '../../../hooks/useApiStatusDispatch'
import { useState, useEffect } from 'react'
import { useGymTrainers } from '../../users/hooks'
import { envAwarePost, capitalize } from '../../../util/util'
import { copyProgram, selectTemplatesModal, setTemplatesModal, setCopyModal } from '../../programs/programsSlice'
import { useHistory } from 'react-router-dom'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import Spinner from 'react-bootstrap/Spinner'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Select from "react-select"
import styles from './TemplatesModal.module.css'


const TemplateDetails = ({ details, length }) => {
    const maxNumWeeks = 2
    const numRows = Math.ceil(length / 7)
    const previewText = (
        numRows > maxNumWeeks ?
            `${maxNumWeeks}-week preview` :
            'Preview'
    )

    //map from day to events on that day
    const dayToEvents = {}
    const eventTypes = ['workouts', 'tasks', 'nutritions']
    eventTypes.forEach(type => {
        const events = details[type]
        events.forEach(event => {
            if (!dayToEvents[event.day]) {
                dayToEvents[event.day] = {}
            }
            if (!dayToEvents[event.day][type]) {
                dayToEvents[event.day][type] = []
            }
            dayToEvents[event.day][type].push(event)
        })
    })

    return (
        <div>
            <small>{previewText}</small>
            <div style={{ maxHeight: '256px', overflow: 'auto' }}>
                {[...Array(Math.min(numRows, maxNumWeeks))].map((_, week) => (
                    <div className="d-flex">
                        {[...Array(7)].map((_, dayOfWeek) => {
                            const day = week * 7 + dayOfWeek + 1
                            const eventsTypeMap = dayToEvents[day]
                            return (
                                <div className={styles.PreviewDay}>
                                    <small className="text-muted" style={{ fontSize: '11px' }}>
                                        Day {day}
                                    </small>
                                    {eventsTypeMap && Object.entries(eventsTypeMap).map(([type, events]) => (
                                        <>
                                            <small style={{ borderBottom: '1px solid #ccc' }}>
                                                {capitalize(type)}
                                            </small>
                                            {events.map(e => <small className="text-muted">{e.eventName}</small>)}
                                        </>
                                    ))}
                                </div>
                            )
                        })}
                    </div>
                ))}
            </div>
        </div>
    )
}

const Template = ({ name, notes, length, id }) => {
    const dispatch = useDispatch()
    const statusDispatch = useApiStatusDispatch()
    const [showDetails, setShowDetails] = useState(false)
    const [details, setDetails] = useState(false)
    const [status, setStatus] = useState('init')
    const user = useSelector(state=>state.users.current_user)
    const handleViewDetails = () => {
        setShowDetails(!showDetails)
        if (!details && status !== 'pending') {
            setStatus('pending')
            envAwarePost('api/programs/api/', {
                action: 'getAllProgramEvents',
                program_id: id
            }).then( res => {
                setStatus('idle')
                if (res.success === '1') {
                    setDetails(res.data)
                } else {
                    window.alert(res.message)
                }
            })
        }
    }

    const [copyStatus,setCopyStatus] = useState('idle')
    const handleCopy = () => {
        setCopyStatus('pending')
        statusDispatch(copyProgram({program_id:id,tgt_user_id_number:user.user_id_number}),'program','create')
        .then(({payload:{success,data}}) => {
            setCopyStatus('idle')
        })
    }

    return (
        <div className={styles.Template}>
            <div className="d-flex align-items-center justify-content-between flex-wrap">
                <div className="d-flex align-items-center flex-wrap">
                    <strong>{name}</strong> <small className="ml-2">({length} days)</small>
                </div>
                <div className="ml-auto">
                    <Button
                        onClick={handleViewDetails}
                        variant="outline-info"
                        className="mr-1"
                        size="sm"
                    >
                        View Details
                    </Button>
                    <OverlayTrigger overlay={
                        <Tooltip>Add To My Programs</Tooltip>
                    }>
                        <Button
                            disabled={copyStatus==='pending'}
                            onClick={handleCopy}
                            variant="outline-primary"
                            size="sm"
                        >
                            {copyStatus!=='pending'&&<i className="fas fa-copy"></i>}
                            {copyStatus==='pending'&&<Spinner animation="border" size="sm"/>}
                        </Button>
                    </OverlayTrigger>
                </div>
            </div>
            <small>{notes}</small>
            {status === "pending" &&
                <div className="text-center">
                    <Spinner animation="border" size="sm" />
                </div>
            }
            {(showDetails && details) &&
                <TemplateDetails details={details} length={length} />
            }
        </div>
    )
}

const TemplatesModal = () => {
    const dispatch = useDispatch()
    const { show, program_id } = useSelector(selectTemplatesModal)
    const [templates, setTemplates] = useState([])
    const [status, setStatus] = useState('pending')
    const statusDispatch = useApiStatusDispatch()
    const [filteredTemplates, setFilteredTemplates] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        setStatus('pending')
        if (show) {
            envAwarePost(
                'api/programs/api/',
                { action: 'getProgramTemplates' }
            ).then(res => {
                setStatus('idle')
                if (res.success === '1') {
                    setTemplates(res.data.programs)
                } else {
                    window.alert(res.message)
                }
            })
        }
    }, [show])

    const toggleCopyModal = () => {
        dispatch(setTemplatesModal({ show: !show }))
    }

    useEffect(() => {
        if (searchQuery) {
            let cleanedQuery = searchQuery.toLowerCase().trim();
            let filtered = templates.filter(item => {
                return item.programName.toLowerCase().includes(cleanedQuery);
            })
            setFilteredTemplates(filtered);
        } else {
            setFilteredTemplates(templates);
        }
    }, [templates, searchQuery])

    return (
        <Modal
            onHide={toggleCopyModal}
            show={show}
            size="lg"
        >
            <Modal.Header closeButton>
                <Modal.Title className="font-weight-normal">
                    Program Templates
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Control value={searchQuery} placeholder="Search Template" onChange={(e) => setSearchQuery(e.target.value)} />

                {status === "pending" &&
                    <div className="text-center">
                        <Spinner animation="border" size="sm" />
                    </div>
                }
                {filteredTemplates.map(t => (
                    <Template name={t.programName} notes={t.programNotes} id={t.programID} length={t.programLength} />
                ))}
            </Modal.Body>
        </Modal>
    )
}

export default TemplatesModal